import { supabase } from '@/lib/supabase'
import type { User } from '@/models/supabase/User'
import { userProfileToUser } from '@/models/supabase/User'
import { detectLocale } from '@/i18n'

/**
 * Simplified auth service that doesn't rely on triggers
 */
export const authSimpleService = {
    /**
     * Ensure profile exists for current authenticated user
     * Creates profile if it doesn't exist
     */
    async ensureProfileForCurrentUser(): Promise<User> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('No authenticated user');

        // Uniquement un nom RÉEL, fourni par le fournisseur d'identité (Google,
        // Apple, Facebook). Le début de l'adresse e-mail n'est pas un nom : écrit
        // en base, il devient le prénom affiché du parent, et il faut ensuite le
        // réparer ailleurs (commit_onboarding_draft sait justement reconnaître
        // « name = partie avant le @ » pour l'écraser). On ne l'écrit plus.
        const displayName = user.user_metadata?.display_name || user.user_metadata?.full_name || user.user_metadata?.name || '';

        // Try to get existing profile
        const { data: profile } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('id', user.id)
            .single();

        if (profile) {
            const currentName = (profile as { name?: string })?.name?.trim() || '';
            const emailPrefix = (user.email || '').split('@')[0];
            // On répare aussi les profils déjà pollués par l'ancien repli.
            const isGenericOrAnonymous = !currentName || currentName === 'User' || currentName === 'Anonymous User'
                || currentName === 'Utilisateur' || (!!emailPrefix && currentName === emailPrefix);
            const updates: Record<string, unknown> = {};
            if (displayName && displayName !== 'User' && isGenericOrAnonymous) {
                updates.name = displayName;
            }
            // If the language is still the DB default ('fr') and the detected locale differs,
            // update it so the first email (e.g. signup confirmation) uses the right language.
            const detectedLocale = detectLocale();
            if ((profile as { language?: string })?.language === 'fr' && detectedLocale !== 'fr') {
                updates.language = detectedLocale;
            }
            if (Object.keys(updates).length > 0) {
                updates.updated_at = new Date().toISOString();
                await supabase.from('user_profiles').update(updates).eq('id', user.id);
                return userProfileToUser({ ...profile, ...updates });
            }
            return userProfileToUser(profile);
        }

        // Create profile if doesn't exist (use upsert to avoid duplicate key errors)
        const detectedLocale = detectLocale();
        const { data: newProfile, error } = await supabase
            .from('user_profiles')
            .upsert({
                id: user.id,
                name: displayName || null,
                email: user.email || '',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                language: detectedLocale,
                daily_reminder: false,
                point_assignments: false,
                reward_unlocked: false
            }, {
                onConflict: 'id'
            })
            .select()
            .single();

        if (error) {
            // Si c'est une erreur de clé dupliquée, réessayer de récupérer le profil
            if (error.code === '23505') {
                const { data: existingProfile } = await supabase
                    .from('user_profiles')
                    .select('*')
                    .eq('id', user.id)
                    .single();
                if (existingProfile) {
                    return userProfileToUser(existingProfile);
                }
            }
            throw error;
        }
        return userProfileToUser(newProfile);
    },

    /**
     * Get current user with profile check
     */
    async getCurrentUserSimple(): Promise<User | null> {
        try {
            return await this.ensureProfileForCurrentUser();
        } catch (error) {
            console.error('Error getting current user:', error);
            return null;
        }
    },

    /**
     * Envoie l'email de vérification en arrière-plan (sans attendre le résultat)
     */
    sendVerificationEmailAsync(email: string): void {
        // Exécution asynchrone sans attendre le résultat
        supabase.auth.resend({
            type: 'signup',
            email: email
        }).then(({ error }) => {
            if (error) {
                console.warn('Failed to send verification email:', error)
            } else {
                // console.log('Verification email sent to:', email)
            }
        }).catch((error) => {
            console.warn('Unexpected error sending verification email:', error)
        })
    },

    /**
     * Vérifie le statut de vérification email de l'utilisateur
     */
    async checkEmailVerificationStatus(userId: string): Promise<{
        isVerified: boolean,
        email: string,
        canResend: boolean
    }> {
        try {
            // Vérifier dans Supabase Auth
            const { data: { user } } = await supabase.auth.getUser()
            const authEmailVerified = user?.email_confirmed_at != null

            // Vérifier dans le profil utilisateur
            const { data: profile } = await supabase
                .from('user_profiles')
                .select('email')
                .eq('id', userId)
                .single()

            const profileEmailVerified = false // Toujours false car la colonne email_verified n'existe pas
            const isVerified = authEmailVerified || profileEmailVerified

            // Si l'utilisateur n'a pas d'email, considérer comme vérifié (pas de bannière)
            const userEmail = profile?.email || user?.email || ''
            if (!userEmail || userEmail.trim() === '') {
                return {
                    isVerified: true, // Pas de bannière si pas d'email
                    email: '',
                    canResend: false
                }
            }

            return {
                isVerified,
                email: userEmail,
                canResend: !isVerified
            }
        } catch (error) {
            console.error('Error checking email verification status:', error)
            return {
                isVerified: false,
                email: '',
                canResend: true
            }
        }
    },

    /**
     * Marque l'email comme vérifié dans le profil utilisateur
     */
    async markEmailAsVerified(userId: string): Promise<boolean> {
        try {
            const { error } = await supabase
                .from('user_profiles')
                .update({
                    updated_at: new Date().toISOString()
                    // Supprimer email_verified car la colonne n'existe pas
                })
                .eq('id', userId)

            if (error) {
                console.error('Error marking email as verified:', error)
                return false
            }

            // console.log('Email marked as verified for user:', userId)
            return true
        } catch (error) {
            console.error('Unexpected error marking email as verified:', error)
            return false
        }
    }
}